import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABMessageTemplateSelect, ABPromoSelect } from '../lib/ab/select-admin';
import { ABMultiCurrencyInput, ABObjectCount } from '../lib/ab/widgets-admin';
import { CPlan } from '../lib/mdl/c-plan';
import { f_routes } from '../lib/mdl/f-route';
import { PCode, PPromo, PPromoEffect, PPromoKind } from '../lib/mdl/p-promo';
import { pCodeDelete, pCodeIsUsable, pCodesByKind, pCodesCreate, pPromoDiscountDescription, p_promos } from '../lib/mdl/p-promo-admin';
import { UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UIDateInput } from '../lib/ui/component/date';
import { UIDialog } from '../lib/ui/component/dialog';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UIExclusiveToggles, UIMultiToggles, UIRadio } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { UIWarning, showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { $amount, YPaymentAmount } from '../lib/y/amount';
import { YArrayDataSource } from '../lib/y/data-source';
import { xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $form, YForm } from '../lib/y/form';
import { $format } from '../lib/y/format';
import { yGetI18N, yGetI18Ns } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { yLogDiff } from '../lib/y/object';
import { $time } from '../lib/y/time';
import { YId } from '../lib/y/types';

class SubscriptionCard extends UIView {
    private _base_price: YPaymentAmount;
    private _price: HTMLElement;
    private _discounted: HTMLElement;
    private _subtitle: HTMLElement;

    constructor(plan: CPlan) {
        super('div', 'tl-card k-subscription c-light');

        this._base_price = plan.price['SE'];

        this.ui_add($el.DIV('r-card', [
            $el.DIV('r-title', yGetI18N(plan.name)),
            this._price = $el.DIV('r-strike', $format.currency(plan.price['SE'])),
            $el.DIV('r-subtitle', [this._discounted = $el.I(''), this._subtitle = $el.I('')]),
            $el.UL(yGetI18Ns(plan.points)),
            new UIButton({ title: "Välj" }),
            $el.DIV(['r-subscription-image', 'k-' + plan.id])
        ]));
    }

    set subtitle(subtitle: string) {
        this._subtitle.textContent = subtitle;
    }

    set amount(amount: YPaymentAmount) {
        this._discounted.textContent = $format.currency($amount.add(this._base_price, amount)) + ' ';
    }

    set always(always: boolean) {
        this._price.textContent = always ? '' : $format.currency(this._base_price) + ' per månad';
    }
}

class PromoScreen extends UIScreen {
    constructor(private _promo?: PPromo, private _duplicate?: boolean) {
        super();
        this.title = (!_duplicate && _promo?.name) || 'New promo';
    }

    protected override fillContent(content: UIViewSpec): void {
        const promo_value = this._promo;
        const promo = this._duplicate ? undefined : this._promo;
        const kind_value = promo_value?.kind ?? 'signup';

        content.classList.add('tl-admin-page');

        const name = new UITextInput({ required: true });
        const kind_values: UIValue<PPromoKind>[] = [{ key: 'signup', name: 'Signup' }, { key: 'resubscribe', name: 'Resubscribe' }, { key: 'giftcard', name: 'Giftcard' }, { key: 'ppv', name: 'PPV' }];
        const kind = new UISelect(kind_values, { required: true, disabled: true });

        const effect = new UIInputAdapter((e: PPromoEffect, entry) => {
            const container = $el.DIV('l-form');
            let form: YForm<any>;

            if(e.kind == 'subscription') {
                const subscription_price_standard = new ABMultiCurrencyInput(100);
                const subscription_price_plusone = new ABMultiCurrencyInput(100);
                const duration_months = new UINumberInput({ required: true });
                const dont_activate = new UICheckbox({ title: 'Do not activate after subscription' });

                container.ui_add([
                    $el.P('text', 'Initial subscription'),
                    $el.DIV('l-input-row', [
                        new UILabeled(subscription_price_standard, { above: 'Standard price' }),
                        new UILabeled(subscription_price_plusone, { above: 'Plus 1 price' }),
                    ]),
                    new UILabeled(duration_months, { above: 'Number of months' }),
                    dont_activate
                ]);

                form = $form({ kind: $form.noop(), subscription_price: $form({ standard: subscription_price_standard, plusone: subscription_price_plusone }), duration_months, dont_activate });
            } else if(e.kind == 'subscription_discount') {
                const subscription_discount_standard = new ABMultiCurrencyInput(-100);
                const subscription_discount_plusone = new ABMultiCurrencyInput(-100);

                const when_first = new UIRadio({ title: 'First month' });
                const when_until = new UIRadio({ title: 'Until', group: when_first.group });
                const when_months = new UIRadio({ title: 'For', group: when_first.group });
                const when_always = new UIRadio({ title: 'For the duration of subscription', group: when_first.group });

                const until = new UIDateInput;
                until.disabled = true;
                const months = new UINumberInput({ chars: 5 });
                months.disabled = true;

                $on(when_until, 'value', v => {
                    until.disabled = !v;
                    until.required = v;
                });

                $on(when_months, 'value', v => {
                    months.disabled = !v;
                    months.required = v;
                });

                container.ui_add([
                    $el.P('text', 'Subscription discount'),
                    $el.DIV('l-input-row', [
                        new UILabeled(subscription_discount_standard, { above: 'Standard discount' }),
                        new UILabeled(subscription_discount_plusone, { above: 'Plus 1 discount' }),
                    ]),
                    $el.DIV('l-toggle-row', when_first),
                    $el.DIV('l-toggle-row', [when_until, until]),
                    $el.DIV('l-toggle-row', [when_months, months, 'months']),
                    $el.DIV('l-toggle-row', when_always),
                ]);

                when_always.value = true;

                form = $form({
                    kind: $form.noop(),
                    subscription_discount: $form({ standard: subscription_discount_standard, plusone: subscription_discount_plusone }),
                    duration_months: $form.simple_input(() => {
                        if(when_first.value) return 1;
                        if(when_months.value) return months.value || 0;
                        return undefined;
                    }, v => {
                        if(v == 1) {
                            when_first.value = true;
                        } else if(v !== undefined) {
                            when_months.value = true;
                            months.value = v;
                        }
                    }),
                    subscription_discount_end: $form.simple_input(() => {
                        if(when_until.value)
                            return until.value;
                        return undefined;
                    }, v => {
                        if(v) {
                            when_until.value = true;
                            until.value = v;
                        }
                    })
                });
            } else if(e.kind == 'purchase_discount') {
                const discount = new ABMultiCurrencyInput(-100);
                const discount_percent = new UINumberInput({ scale: -1 });

                container.ui_add([
                    $el.P('text', 'Purchase discount'),
                    new UILabeled(discount, { above: 'Discount' }),
                    new UILabeled(discount_percent, { above: 'Discount percent' }),
                ]);

                form = $form({ kind: $form.noop(), discount, discount_percent });
            } else if(e.kind == 'book_session') {
                const session = new UITextInput;

                container.ui_add([
                    $el.P('text', 'Book session'),
                    new UILabeled(session, { above: 'Session id' }),
                ]);

                form = $form({ kind: $form.noop(), session });
            } else {
                throw 'invalid effect kind';
            }

            return {
                view: $el.DIV('o-promo-effect', [
                    container,
                    new UIButton({ icon: UIIcon.TRASH_CAN, on_click: () => { effect.remove(entry) } })
                ]),
                input: form
            };
        });

        const effect_list = new UIList(effect, { classes: 'o-promo-effect-list b' });

        const effect_add_subscription = new UIButton({
            title: '+ Initial subscription', kind: 'k-text', on_click: () => {
                effect.push({
                    kind: 'subscription',
                    subscription_price: { standard: { SEK: 0, EUR: 0 }, plusone: { SEK: 26000, EUR: 2600 } },
                    duration_months: 1,
                    dont_activate: false
                }, true)
            }
        });

        const effect_add_subscription_discount = new UIButton({
            title: '+ Subscription discount', kind: 'k-text', on_click: () => {
                effect.push({
                    kind: 'subscription_discount',
                    subscription_discount: { standard: { SEK: -29000, EUR: -2900 }, plusone: { SEK: -37000, EUR: -3700 } },
                }, true)
            }
        });

        const effect_add_purchase_discount = new UIButton({
            title: '+ Purchase discount', kind: 'k-text', on_click: () => {
                effect.push({
                    kind: 'purchase_discount',
                    discount: { SEK: 0, EUR: 0 },
                    discount_percent: 0,
                }, true)
            }
        });

        const effect_add_book_session = new UIButton({
            title: '+ Book session', kind: 'k-text', on_click: () => {
                effect.push({
                    kind: 'book_session',
                    session: '' as YId,
                }, true)
            }
        });

        const popup_title = new UII18NInput(new UITextInput);
        const popup_subtitle = new UII18NInput(new UITextInput);
        const hero_button = new UII18NInput(new UITextInput);
        const hero_subtitle = new UII18NInput(new UITextInput);
        const card_subtitle = new UII18NInput(new UITextInput);

        const receipt_email = new ABMessageTemplateSelect({ no_template: true, template_channel: 'email', template_context: 'subscription' });
        const welcome_inbox = new ABMessageTemplateSelect({ no_template: true, template_channel: 'inbox', template_context: 'subscription' });
        const reminder_email = new ABMessageTemplateSelect({ no_template: true, template_channel: 'email', template_context: 'subscription' });

        const texts = $form({ popup_title, popup_subtitle, hero_button, hero_subtitle, card_subtitle });
        const messages = $form({ receipt_email, welcome_inbox, reminder_email });

        const form = $form({ name, kind, effect, texts, messages })

        const standard_card = new SubscriptionCard(window.ab_plans[0]);
        const plusone_card = new SubscriptionCard(window.ab_plans[1]);

        $on(card_subtitle, 'value', v => {
            standard_card.subtitle = yGetI18N(v);
            plusone_card.subtitle = yGetI18N(v);
        });

        // $on(when_always, 'value', v => {
        //     standard_card.always = v;
        //     plusone_card.always = v;
        // });

        // // $on(standard_discount, 'value', v => {
        // //     standard_card.amount = $amount.inCurrency(v, cCountryDefaultCurrency(getCountry()));
        // // });

        // // $on(plusone_discount, 'value', v => {
        // //     plusone_card.amount = $amount.inCurrency(v, cCountryDefaultCurrency(getCountry()));
        // // });

        const messages_group = $el.DIV('l-form', [
            $el.H3('new-subheading', 'Messages'),
            new UILabeled(receipt_email, { above: 'Receipt email' }),
            new UILabeled(welcome_inbox, { above: 'In-app welcome message' }),
            new UILabeled(reminder_email, { above: 'Reminder email at end of subscription' }),
        ]);

        const texts_group = $el.DIV('l-form', [
            $el.H3('new-subheading', 'Copy'),
            new UILabeled(hero_button, { above: 'Hero button' }),
            new UILabeled(hero_subtitle, { above: 'Hero image subtitle' }),
            new UILabeled(popup_title, { above: 'Popup title' }),
            new UILabeled(popup_subtitle, { above: 'Popup subtitle' }),
            new UILabeled(card_subtitle, { above: 'Subscription card subtitle' }),
        ]);

        if(kind_value == 'giftcard' || kind_value == 'ppv') {
            effect_add_subscription.ui_hide();
            effect_add_subscription_discount.ui_hide();
            effect_add_book_session.ui_hide();

            messages_group.ui_hide();
            texts_group.ui_hide();
        } else if(kind_value == 'signup' || kind_value == 'resubscribe') {
            effect_add_purchase_discount.ui_hide();

            if(kind_value == 'resubscribe') {
                receipt_email.ui_hide();
                hero_button.ui_hide();
                hero_subtitle.ui_hide();
            } else {
                popup_title.ui_hide();
                popup_subtitle.ui_hide();
            }
        }

        if(promo_value) {
            form.value = promo_value;

            if(promo) {
                content.classList.add('m-sidebar');
                const sidebar = $el.DIV('tl-sidebar')


                sidebar.ui_add([
                    $el.DIV('tl-sidebar-heading', 'Users'),
                    $el.B(promo.usage_count + ''),
                    $el.DIV('tl-sidebar-heading', 'Routes using this promo')
                ]);

                f_routes.getAll().then(routes => {
                    routes = routes.filter(r => r.promo_id == promo.id);

                    if(routes.length) {
                        for(const r of routes) {
                            sidebar.ui_add($el.B(r.path));
                        }
                    } else {
                        sidebar.ui_add($el.B('No routes'));
                    }
                });

                content.ui_add(sidebar);
            }
        } else {
            hero_button.value = { sv: 'Prova en månad för $2$' };
            hero_subtitle.value = { sv: 'Första månaden $2$. Avsluta när du vill.', en: 'First month $2$. End when you want' };
            popup_title.value = { sv: 'Prova en månad för $2$' };
            popup_subtitle.value = { sv: 'Första månaden $2$. Avsluta när du vill.', en: 'First month $2$. End when you want' };
            card_subtitle.value = { sv: '$2$ första månaden' };
        }

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(name, { above: 'Name' }),
            new UILabeled(kind, { above: 'Promo kind' }),
            $el.H3('subheading', 'Promo effects'),
            effect_list,
            $el.DIV('l-button-row m-left', [
                effect_add_subscription,
                effect_add_subscription_discount,
                effect_add_purchase_discount,
                effect_add_book_session,
            ]),
            texts_group,
            messages_group,
        ]);

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                const value = form.value;
                if(!value) return;
                yLogDiff(promo, value);

                p_promos.upsert(promo?.id, value).then($location.pop, uiToast);
            }
        });
        addTooltip(promo ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, button]);
    }
}

export class AdminPromosScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const doSearch = () => {
            const promos = p_promos.all.filter(p => (p.is_active && !p.is_hidden) || xavis_experiments);
            count.number = promos.length;
            source.data = promos;
        }

        const count = new ABObjectCount('promos');

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<PPromo>();
        def.addValue(p => p.name, { title: 'Name', width: 100 });
        def.addValue(p => p.kind, { title: 'Kind', width: 100 });
        def.addValue(pPromoDiscountDescription, { title: 'Prices', width: 150 });
        def.addValue(p => p.usage_count, $fmt.NUMBER, { title: 'Users', width: -120 });
        if(xavis_experiments) {
            def.addButtons([
                {
                    icon: UIIcon.COPY, tooltip: 'Duplicate', action: item => {
                        $location.push({ transient: new PromoScreen(item, true) });
                    }
                },
                {
                    icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                        $location.push({ transient: new PromoScreen(item) });
                    }
                },
            ], { width: -62 });
        }

        def.preference_prefix = 'promos';

        const source = new YArrayDataSource<PPromo>();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New tag', () => { $location.push({ transient: new PromoScreen }) })
        ]);

        doSearch();
        this.onViewDidReappear = doSearch;
    }
}

class AddPromoCodeDialog extends UIDialog<PCode[]> {
    constructor() {
        super({ close: true, classes: 'm-500' });

        const specific = new UIExclusiveToggles([true, false], m => new UIRadio({ key: m, title: m ? 'Specific code' : 'Random codes' }), { classes: 'l-toggle-row' });
        const amount = new UINumberInput({ placeholder: 'Amount', required: true });
        const code = new UITextInput({ placeholder: 'Code', required: true });

        const promo = new ABPromoSelect({ placeholder: 'Promo', required: true });
        const max_uses = new UINumberInput({ placeholder: 'Max uses (unlimited if empty)' });
        const expires = new UIDateInput({ placeholder: 'Expires (optional)' });

        const country_values = [{ key: 'SE', title: 'Sweden' }, { key: 'DE', title: 'Germany' }];
        const countries = new UIMultiToggles(country_values, v => new UICheckbox(v), { required: true, classes: 'l-form' });
        countries.value = ['SE', 'DE'];

        const specific_form = $form({ code: code, promo: promo, expires: expires, max_uses: max_uses });
        const random_form = $form({ amount: amount, promo: promo, expires: expires, max_uses: max_uses });

        $on(specific, 'value', v => {
            code.ui_show(undefined, !!v);
            amount.ui_show(undefined, !v);
        });

        specific.value = true;

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('heading', 'Add promo codes'),
            specific,
            code,
            amount,
            promo,
            max_uses,
            expires,
            $el.DIV('l-button-row', new UIButton({
                title: "Create", classes: 'm-round m-compact', on_click: () => {
                    const form = specific.value ? specific_form : random_form;
                    if(!form.validate()) return;
                    pCodesCreate(form.value).then(codes => {
                        const copy = new UIButton({
                            title: 'COPY TO CLIPBOARD', kind: 'k-text', on_click: () => {
                                navigator.clipboard.writeText(codes.map(c => c.code).join('\n')).then(() => {
                                    uiToast('Codes copied to clipboard');
                                })
                            }
                        });

                        new UIWarning({
                            content: [
                                $el.H2('title', 'Codes created'),
                                codes.map(c => $el.B(c.code)),
                                copy
                            ], cancel_text: null
                        }).present({ replace: this }).then(() => {
                            this.dismiss(codes);
                        })
                    });
                }
            }))
        ]));
    }
}

export class AdminCodesScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        let all_codes = (await pCodesByKind('promo')).filter(c => pCodeIsUsable(c));

        const doSearch = () => {
            count.number = all_codes.length;
            source.data = all_codes;
        }

        const count = new ABObjectCount('codes');

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        // const type_select = new UISelect<PCodeKind>([{ key: 'promo', name: 'Promo' }, { key: 'giftcard', name: 'Gift card' }, { key: 'referral', name: 'Referral' }]);
        // type_select.value = 'promo';

        // this.search_bar.ui_prepend([type_select]);

        const def = new UITableDefinition<PCode>();
        def.addValue(c => c.code, { title: 'Code', width: 100 });
        def.addValue(c => {
            const promo = p_promos.maybe(c.promo_id!);
            if(promo)
                return promo.kind + ' - ' + promo.name + ' - ' + pPromoDiscountDescription(promo)
            else
                return '-';
        }, { title: 'Promo', width: 150 });
        def.addValue(c => c.usage_max ? '' + c.usage_max : 'Unlimited', { title: 'Max uses', width: -100 });
        def.addValue(c => c.usage_count + ' times', { title: 'Used', width: -100 });
        def.addValue(c => $time(c.expires_at), $fmt.DATE(), { title: 'Expires', width: -120 });
        def.addValue(c => $time(c.created_at), $fmt.DATE(), { title: 'Created at', width: -120 });
        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', when: r => r.kind != 'route', action: (item, row) => {
                    return showWarning({ text: "Are you sure you want to delete this code?" }).then(() => {
                        pCodeDelete(item.code).then(() => {
                            row.remove();
                        })
                        // return $tl.codes.remove(item.id).then(() => { doSearch() });
                    });
                }
            },
        ], { width: -60 });

        def.controller = row => {
            const expires = $time(row.item.expires_at);

            if(expires?.is_past || (row.item.usage_max && row.item.usage_count >= row.item.usage_max))
                row.classList.add('tl-canceled-row');

            return undefined;
        }

        def.preference_prefix = 'codes';

        const source = new YArrayDataSource<PCode>();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New code', () => { new AddPromoCodeDialog().present().then(doSearch) })
        ]);

        doSearch();
        this.onViewDidReappear = () => {
            pCodesByKind('promo').then(codes => {
                all_codes = codes.filter(c => pCodeIsUsable(c));
                doSearch();
            });
        };
    }
}